/**
 * SF-4651: page for one demo build (/demos/:id). It shows what was asked for and the status; the
 * review (SF-4652) and progress (SF-4653) views replace the body. While the build waits for its
 * transcripts it sends them and offers the ways on; what sf-api answered for each file stays on
 * the page once Eva drafts, and a draft that ran out of time can be drafted again (SF-4729).
 * SF-4746: any failed draft can be drafted again and a failed or cancelled one dismissed, when
 * sf-api says so; someone else's build (All demos) is read only, with every action hidden. A build
 * past its review links to its plan and chat, read only.
 */
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { useCan } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Descriptions, Result, Space, Spin } from 'antd';
import { AwaitingTranscripts } from './components/AwaitingTranscripts';
import { DraftAgain } from './components/DraftAgain';
import { DismissDemo } from './components/DismissDemo';
import { ReadOnlyNote } from './components/ReadOnlyNote';
import { TranscriptOutcomes } from './components/TranscriptOutcomes';
import { EnvironmentBanner, EnvironmentTag } from './components/EnvironmentBadge';
import { StatusTag } from './components/StatusTag';
import {
  demoBuildQueryKey,
  demoErrorMessage,
  isDemoBuildGone,
  useDemoBuild,
  type DemoBuild,
} from './api/demoBuildsClient';
import { demoProgressPath, demoReviewPath, hasRun, isPastReview, startedBy } from './constants';
import { useCanActOn } from './useCanActOn';
import { useUploadOutcomes } from './transcripts';

// SF-4652: statuses whose change set can be reviewed (or is on its way).
const REVIEWABLE = ['drafting', 'ready_for_review'];

export const DemoDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: can, isLoading: canLoading } = useCan({ resource: 'demos', action: 'show' });
  const allowed = can?.can === true;
  const query = useDemoBuild(id, allowed);
  const queryClient = useQueryClient();
  const outcomes = useUploadOutcomes(id);
  const canActOn = useCanActOn();

  /**
   * A POST answer for a build goes into that build's cache entry (dropping a read already under
   * way), then that entry is read again. Keyed by the build the answer is for, so a late answer
   * never lands on another build's page.
   */
  const applyBuild = async (buildId: string, answer?: DemoBuild) => {
    const key = demoBuildQueryKey(buildId);
    if (answer?.id === buildId && answer.status) {
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData<{ data: DemoBuild }>(key, (old) =>
        old ? { ...old, data: { ...old.data, ...answer } } : old,
      );
    }
    await queryClient.invalidateQueries({ queryKey: key, exact: true });
  };

  if (canLoading) return <Spin />;
  if (!allowed) {
    return <Result status="403" title="403" subTitle="You do not have permission to see demos." />;
  }
  // A 403 or 404 means the build is gone or no longer this person's: its cached copy is not shown.
  const gone = query.isError && isDemoBuildGone(query.error);
  const build = gone ? undefined : query.data?.data;
  // SF-4653: a build whose run has started opens on its progress page.
  if (id && build && build.id === id && hasRun(build)) {
    return <Navigate to={demoProgressPath(id)} replace />;
  }
  const current = build && build.id === id ? build : undefined;
  const readOnly = !canActOn(current);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <EnvironmentBanner />
      <Card
        title="Demo"
        extra={
          <Space>
            {build && REVIEWABLE.includes(build.status) && (
              <Button type="primary" onClick={() => navigate(demoReviewPath(build.id))}>
                Review the change set
              </Button>
            )}
            {build && isPastReview(build.status) && (
              <Button onClick={() => navigate(demoReviewPath(build.id))}>View plan and chat</Button>
            )}
            <Button onClick={() => navigate('/demos')}>Back to demos</Button>
          </Space>
        }
      >
        {query.isLoading && <Spin />}
        {/* A passing read failure keeps the build already on screen (React Query keeps the last
            data) with a warning; a build that never loaded, or is gone (403/404), is an error. */}
        {query.isError && !build && (
          <Alert
            type="error"
            showIcon
            message={demoErrorMessage(query.error, 'Could not load this demo.')}
          />
        )}
        {query.isError && build && (
          <Alert
            type="warning"
            showIcon
            message="Could not refresh this demo. Showing what was last loaded."
          />
        )}
        {current && !current.is_mine && (
          <div style={{ marginBottom: 16 }}>
            <ReadOnlyNote creator={current.created_by} />
          </div>
        )}
        {build && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Prospect">{build.inputs?.domain}</Descriptions.Item>
            <Descriptions.Item label="Area">
              {build.inputs?.area?.label ?? '—'} ({build.inputs?.area?.radius_miles} miles, up to{' '}
              {build.inputs?.area?.max_sites} locations)
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <StatusTag status={build.status} dismissed={!!build.dismissed_at} />
            </Descriptions.Item>
            <Descriptions.Item label="Started by">{startedBy(build.created_by)}</Descriptions.Item>
            <Descriptions.Item label="Environment">
              <EnvironmentTag env={build.environment} />
            </Descriptions.Item>
            {build.error && <Descriptions.Item label="Problem">{build.error}</Descriptions.Item>}
          </Descriptions>
        )}
        {current && !readOnly && current.can_draft_again && (
          <div style={{ marginTop: 16 }}>
            <DraftAgain key={current.id} buildId={current.id} onBuild={applyBuild} />
          </div>
        )}
        {current && !readOnly && current.can_dismiss && (
          <div style={{ marginTop: 16 }}>
            <DismissDemo key={current.id} buildId={current.id} onBuild={applyBuild} />
          </div>
        )}
      </Card>
      {current && !readOnly && current.status === 'awaiting_transcripts' && (
        <AwaitingTranscripts key={current.id} build={current} onBuild={applyBuild} />
      )}
      {/* The waiting panel shows them itself; once Eva drafts they stay here. */}
      {build &&
        build.id === id &&
        build.status !== 'awaiting_transcripts' &&
        outcomes.length > 0 && (
          <Card title="Transcripts sent">
            <TranscriptOutcomes outcomes={outcomes} />
          </Card>
        )}
    </Space>
  );
};
